import { cx } from 'class-variance-authority';
import React from 'react';

import './paginator.scss';

type RangeProps = {
   firstIndex: number;
   lastIndex: number;
};

type PaginationProps = {
   totalPages: number;
   currentPage: number;
   onPageClick?: (pageNumber: number) => void;
   setRange: (state: RangeProps) => void;
   range: RangeProps;
   disabled?: boolean;
};

const PAG_PAGES = 5;

export const Paginator = ({
   totalPages,
   currentPage,
   onPageClick,
   setRange,
   range,
   disabled = false,
}: PaginationProps) => {
   const safeTotalPages = Math.max(0, Math.floor(totalPages));
   const safeCurrentPage = Math.min(
      Math.max(Number.isFinite(currentPage) ? Math.floor(currentPage) : 0, 0),
      Math.max(safeTotalPages - 1, 0)
   );
   const firstIndex = Math.floor(safeCurrentPage / PAG_PAGES) * PAG_PAGES;
   const lastIndex = Math.min(firstIndex + PAG_PAGES, safeTotalPages);
   const visiblePages = Array.from({ length: safeTotalPages }, (_, i) => i + 1).slice(
      firstIndex,
      lastIndex
   );

   React.useEffect(() => {
      if (range.firstIndex === firstIndex && range.lastIndex === lastIndex) {
         return;
      }

      setRange({
         firstIndex,
         lastIndex,
      });
   }, [firstIndex, lastIndex, range.firstIndex, range.lastIndex, setRange]);

   const onNextClick = () => {
      if (disabled || lastIndex >= safeTotalPages) {
         return;
      }

      const nextFirstIndex = firstIndex + PAG_PAGES;
      const nextLastIndex = Math.min(nextFirstIndex + PAG_PAGES, safeTotalPages);
      onPageClick?.(nextFirstIndex);
      setRange({
         firstIndex: nextFirstIndex,
         lastIndex: nextLastIndex,
      });
   };

   const onPrevClick = () => {
      if (disabled || firstIndex === 0) {
         return;
      }

      const newFirst = Math.max(firstIndex - PAG_PAGES, 0);
      const newLast = Math.min(newFirst + PAG_PAGES, safeTotalPages);
      onPageClick?.(newLast - 1);

      setRange({
         firstIndex: newFirst,
         lastIndex: newLast,
      });
   };

   return (
      <div className="paginator">
         <ul className="paginator__list">
            <li>
               <button
                  className={cx('pag-trigger-btn', 'pag-trigger-btn--prev')}
                  onClick={onPrevClick}
                  disabled={disabled || firstIndex === 0}
               >
                  {`<`}
               </button>
            </li>

            {visiblePages.map((pageNumber: number) => (
               <li key={pageNumber}>
                  <button
                     onClick={() => {
                        if (disabled) {
                           return;
                        }

                        onPageClick?.(pageNumber - 1);
                     }}
                     className={cx(
                        'pag-item',
                        safeCurrentPage === pageNumber - 1 && 'pag-item--active'
                     )}
                     disabled={disabled}
                  >
                     {pageNumber}
                  </button>
               </li>
            ))}

            <li>
               <button
                  className={cx('pag-trigger-btn', 'pag-trigger-btn--next')}
                  onClick={onNextClick}
                  disabled={disabled || lastIndex >= safeTotalPages}
               >
                  {`>`}
               </button>
            </li>
         </ul>
      </div>
   );
};
